<script setup>

import {onMounted, ref, watch} from "vue";

const courses = ref([]);
const addingNewCourseType = ref(null)
const addingNewCourseState = ref('')

const newCourse = ref({
    title: '',
    type: null,
    provider_id: null,
    description: '',
    requires_license: true,
})

const loadCourses = async () => {
    const res = await axios.get(route('api.course.backoffice.index'));
    courses.value = res.data.courses
}

const saveNewCourse = async () => {
    addingNewCourseState.value = 'waiting'
    const res = await axios.post(route('api.course.backoffice.store'), newCourse.value);
    if (res.data.success) {
        addingNewCourseType.value = null
        window.location.href = res.data.redirect_url
    } else {
        addingNewCourseState.value = 'error'
    }
}

watch (addingNewCourseType, (newType) => {
    newCourse.value.type = addingNewCourseType.value
})

onMounted(() => {
    loadCourses();
})

</script>
<template>
<div>
    <div class="md:sticky top-2 z-topbar">
        <frosted-bar>
            <div class="flex items-center justify-between space-x-4 f">
                <h1>{{ _mft('course:domainNamePlural') }}</h1>

                <div class="flex items-center space-x-4">
                    <story-lane-modal :module="'content-v2'"></story-lane-modal>

                    <button-more-options
                        :label="_mft('course:newContent')"
                        :cancel-on-outside-click="false"
                    >
                        <button-more-options-option
                            @click="addingNewCourseType = 'class'"
                        >
                            {{ _mft('course:newCourse') }}
                        </button-more-options-option>
                        <button-more-options-option
                            @click="addingNewCourseType = 'feed'"
                        >
                            {{ _mft('course:newFeed') }}
                        </button-more-options-option>
                    </button-more-options>
                </div>
            </div>
        </frosted-bar>
    </div>

    <ui-tabbed :display-in-tab-bar="true">
        <ui-tabbed-tab
            :value="'class'"
            :active="true"
            :title="_mft('course:tab.courses.title')"
        >
            <card-basic>
                <list-basic list-class="-mx-4">
                    <list-basic-item
                        v-for="course in courses.filter(c => c.type === 'class')"
                        :key="course.id"
                        :highlight-on-hover="true"
                    >
                        <template #status>
                        </template>
                        <template #title>
                            <a
                                class="font-medium text-gray-900 truncate mf-underline"
                                :href="route('course.backoffice.edit', course.id) + '#chapters'"
                            >
                                {{ course.title }}
                            </a>
                        </template>
                        <template #actions>
                            <a
                                class="font-medium text-gray-900 truncate mf-underline"
                                :href="route('course.backoffice.edit', course.id) + '#chapters'"
                            >
                                <button-secondary
                                    size="sm"
                                    class="mb-0.5"
                                >
                                    {{ _mft('shared:action.edit') }}
                                </button-secondary>
                            </a>
                        </template>
                    </list-basic-item>
                </list-basic>
            </card-basic>

        </ui-tabbed-tab>
        <ui-tabbed-tab
            :value="'feed'"
            :title="_mft('course:tab.feeds.title')"
        >
            <card-basic>
                <list-basic list-class="-mx-4">
                    <list-basic-item
                        v-for="course in courses.filter(c => c.type === 'feed')"
                        :key="course.id"
                        :highlight-on-hover="true"
                    >
                        <template #status>
                        </template>
                        <template #title>
                            <a
                                class="font-medium text-gray-900 truncate mf-underline"
                                :href="route('course.backoffice.edit', course.id) + '#chapters'"
                            >
                                {{ course.title }}
                            </a>
                        </template>
                        <template #actions>
                            <a
                                class="font-medium text-gray-900 truncate mf-underline"
                                :href="route('course.backoffice.edit', course.id) + '#chapters'"
                            >
                                <button-secondary
                                    size="sm"
                                    class="mb-0.5"
                                >
                                    {{ _mft('shared:action.edit') }}
                                </button-secondary>
                            </a>
                        </template>
                    </list-basic-item>
                </list-basic>
            </card-basic>
        </ui-tabbed-tab>
    </ui-tabbed>

    <slideout-with-slots
        v-if="addingNewCourseType"
        @close="addingNewCourseType = null"
        :title="addingNewCourseType === 'class' ? _mft('course:newCourse') : _mft('course:newFeed')"
    >
        <template #actions>
            <div class="mb-2 flex justify-end space-x-2">
                <button-secondary
                    @click="addingNewCourseType = null"
                >
                    {{ _mft('shared:cancel') }}
                </button-secondary>
                <button-success
                    @click="saveNewCourse"
                >
                    {{ _mft('shared:action.save') }}
                </button-success>
            </div>
        </template>
        <template #body>
            <input-text
                v-model:content="newCourse.title"
                :label="_mft('course:input.title')"
                autofocus
            ></input-text>

            <toggle-switch
                v-if="newCourse.type === 'feed'"
                v-model:content="newCourse.requires_license"
                label="course:setting.requiresLicense.label"
            />
            <p
                v-if="newCourse.type === 'feed'"
                class="text-xs italic -mt-3"
            >
                {{
                    newCourse.requires_license
                        ? _mft('course:setting.requiresLicense.descriptionEnabled')
                        : _mft('course:setting.requiresLicense.descriptionDisabled')
                }}
            </p>

            <input-editor
                v-model:content="newCourse.description"
                :label="_mft('course:input.description')"
            ></input-editor>
        </template>
    </slideout-with-slots>

</div>
</template>
